import { html, LitElement, PropertyValues, TemplateResult } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { API_TYPE_POLICY_FILTER, WEB_COMPONENT_ASSIGN_POLICY } from '../../../../../webcomponents.constants';
import { styles } from './view/styles';
import { FontSizes, FontWeights, LabelWidthValues, LineHeights } from '../../../../../types/common/types';
import { AssignPolicyFormValue, PolicyAssignationType } from './types';
import { translateFn } from '../../../../../utils/translator.helper';
import { toggleDisabled, toggleVisibility } from '../../../../../utils/htmlElement.utils';
import { ChildGroupsGridWebComponent } from '../childGroupsGrid';
import { GzSingleSelectComplexWebComponent } from '../../../../../ui/gzSingleSelectComplex';
import { SingleSelectComplexEvents } from '../../../../../ui/gzSingleSelectComplex/types';
import { ListItem } from '../../../../../ui/gzSingleSelectList/types';
import { GzFormFieldWebComponent } from '../../../../../ui/gzForm/formField';
import { GzRadioGroupWebComponent } from '../../../../../ui/gzRadio';
import { FormGroup } from '../../../../../ui/gzForm/formGroup/form-group';
import { GzCheckboxWebComponent } from '../../../../../ui/gzCheckbox';
import { TargetsGridWebComponent } from '../targetsGrid';
import { InfoBoxSeverity } from '../../../../../ui/gzInfoBox/types';
import { ButtonFontSizes, ButtonHeights, ButtonThemes, ButtonTypes } from '../../../../../ui/gzButton/types';
import { NetworkPopUpEvents } from '../../NetworkBasePopUp';
import { ProtectedEntity } from '@nirvana/shared/dtos/types/protected-entities/common';

@customElement(WEB_COMPONENT_ASSIGN_POLICY)
export class GzAssignPolicyWebComponent extends LitElement {
  @property({ type: Array, attribute: false, reflect: true })
  targets: Array<ProtectedEntity>;

  @query('gz-form-field#assignation-group')
  policyAssignTypeFormField: GzFormFieldWebComponent<GzRadioGroupWebComponent>;

  @query('div#extra-info-custom-assignation')
  customAssignationContainer: HTMLElement;

  @query('div#child-groups-grid')
  childGroupsGridContainer: HTMLElement;

  @query('gz-child-groups-grid')
  childGroupsGrid: ChildGroupsGridWebComponent;

  @query('gz-targets-grid')
  targetsGrid: TargetsGridWebComponent;

  @query('gz-form-field#policy-filter-form-field')
  policyFilterFormField: GzFormFieldWebComponent<GzSingleSelectComplexWebComponent>;

  @query('gz-form-field#child-groups-form-field')
  enforcedFormField: GzFormFieldWebComponent<GzCheckboxWebComponent>;

  @query('gz-single-select-complex#master-policy-filter')
  policyFilter;

  private policyFilterInitialItems: Array<ListItem> = [];
  private formGroup: FormGroup;
  private enforcedTargetIds: Array<string> | undefined = undefined;

  static get styles() {
    return styles;
  }

  protected firstUpdated(_changedProperties: PropertyValues) {
    super.firstUpdated(_changedProperties);
    this.buildFormGroup();
    this.buildGrids();
    this.policyFilter.setApiType(API_TYPE_POLICY_FILTER);
    this.policyFilter.setFilterOptions(undefined);
    this.policyFilter.addEventListener(
      `${this.policyFilter.filterType}_${SingleSelectComplexEvents.BACKEND_OPTIONS_LOADED}`,
      this.handlePolicyFilterBackendLoaded.bind(this)
    );
  }

  private buildFormGroup() {
    this.formGroup = FormGroup.build({
      assignationType: this.policyAssignTypeFormField,
      policyId: this.policyFilterFormField,
      enforced: this.enforcedFormField,
    });
  }

  public getFormValue(): AssignPolicyFormValue {
    return this.formGroup.value as unknown as AssignPolicyFormValue;
  }

  private buildGrids() {
    this.childGroupsGrid.injectDependencies({ translateFn: translateFn, targets: this.targets });
    this.childGroupsGrid.start().then().catch(console.error);
    this.targetsGrid.injectDependencies({ translateFn: translateFn, targets: this.targets });
    this.targetsGrid.start().then().catch(console.error);
  }

  private renderDescription(): TemplateResult {
    return html`
      <div id="description-container">
        <gz-text lineHeight="${LineHeights.BIG}" fontSize="${FontSizes.HEADING_6}"
          >${translateFn('Select the assignment method:')}</gz-text
        >
      </div>
    `;
  }

  private renderExtraInfoForCustomAssignation(): TemplateResult {
    return html`
      <div id="extra-info-custom-assignation">
        <gz-form-field id="policy-filter-form-field">
          <gz-single-select-complex
            gz-form-input
            id="master-policy-filter"
            placeholder="${translateFn('Select policy')}"
            noSearchResultsString="${translateFn('No policy found.')}"
            labelWidth="${LabelWidthValues.XLARGE}"
            cannotDelete
          >
          </gz-single-select-complex>
        </gz-form-field>
        <gz-form-field id="child-groups-form-field">
          <gz-checkbox
            gz-form-input
            id="force-policy-to-child-groups"
            label="${translateFn('Force policy inheritance to nested entities')}"
            @change="${this.handleForceInheritanceToggle}"
          >
          </gz-checkbox>
        </gz-form-field>
        <gz-text
          id="force-inheritance-text-info"
          class="force-inheritance-text-info helper-text"
          lineHeight="${LineHeights.NORMAL}"
          fontSize="${FontSizes.PARAGRAPH_12}"
        >
          ${translateFn(
            'The current policies of all nested entities, such as groups and endpoints, will be replaced with the selected policy.'
          )}
        </gz-text>
      </div>
    `;
  }

  private renderRadioGroup(): TemplateResult {
    return html`
      <div id="assignation-radio-group">
        <gz-form-field id="assignation-group" .disabled="${this.getNumberOfEnforcedTargets() !== 0}">
          <gz-radio-group
            class="pl-5"
            name="assignation"
            gz-form-input
            groupName="assignation-group"
            .selectedOption="${PolicyAssignationType.CUSTOM}"
            @radioSelectionChanged="${this.handleAssignationTypeChanged}"
          >
            <gz-radio
              name="assignation-group"
              .value="${PolicyAssignationType.CUSTOM}"
              label="${translateFn('Use specific policy:')}"
            >
            </gz-radio>
            ${this.renderExtraInfoForCustomAssignation()}
            <gz-radio
              name="assignation-group"
              .value="${PolicyAssignationType.INHERITED}"
              label="${translateFn('Inherit policy from above')}"
            >
            </gz-radio>
          </gz-radio-group>
        </gz-form-field>
      </div>
    `;
  }

  private renderCannotApplyChanges(): TemplateResult {
    const totalEnforcedTargets = this.getNumberOfEnforcedTargets();
    return html`
      <div class="mt-3 mb-3 ml-5 ${totalEnforcedTargets === 0 ? 'hidden' : ''}" id="cannot-apply-changes-container">
        <gz-info-box severity="${InfoBoxSeverity.WARNING}">
          ${this.targets.length === totalEnforcedTargets
            ? translateFn('Cannot apply changes. All {totalTargets} targets have forced policy inheritance.', {
                totalTargets: this.targets.length,
              })
            : translateFn(
                'Cannot apply changes. {totalEnforcedTargets} of {totalTargets} targets have forced policy inheritance.',
                {
                  totalEnforcedTargets: totalEnforcedTargets,
                  totalTargets: this.targets.length,
                }
              )}
        </gz-info-box>
        <gz-button
          class="ml-2"
          type="${ButtonTypes.OUTLINE}"
          buttonHeight="${ButtonHeights.SMALL}"
          theme="${ButtonThemes.LIGHT}"
          buttonFontSize="${ButtonFontSizes.SMALL}"
          .disabled="${this.targets.length === totalEnforcedTargets}"
          @click="${this.handleExcludeTargets}"
        >
          ${translateFn('Exclude these targets')}
        </gz-button>
      </div>
    `;
  }

  private renderChildGroupsGrid(): TemplateResult {
    return html`
      <div class="hidden child-groups-grid" id="child-groups-grid">
        <gz-title
          fontWeight="${FontWeights.MEDIUM}"
          fontSize="${FontSizes.HEADING_4}"
          lineHeight="${LineHeights.NORMAL}"
        >
          ${translateFn('Nested entities')}
        </gz-title>
        <gz-child-groups-grid></gz-child-groups-grid>
      </div>
    `;
  }

  private renderTargetsGrid(): TemplateResult {
    return html`
      <div id="targets-grid" class="targets-grid">
        <gz-title
          fontWeight="${FontWeights.MEDIUM}"
          fontSize="${FontSizes.HEADING_4}"
          lineHeight="${LineHeights.NORMAL}"
        >
          ${translateFn('Selected targets')}
        </gz-title>
        <gz-targets-grid></gz-targets-grid>
      </div>
    `;
  }

  protected render(): unknown {
    return html`
      ${this.renderDescription()} ${this.renderRadioGroup()} ${this.renderCannotApplyChanges()}
      ${this.renderChildGroupsGrid()} ${this.renderTargetsGrid()}
    `;
  }

  private handleForceInheritanceToggle(event: CustomEvent) {
    event.stopImmediatePropagation();
    const value = !!event.detail?.checked;
    toggleVisibility(this.childGroupsGridContainer, value);
  }

  private handleAssignationTypeChanged(event: CustomEvent) {
    event.stopImmediatePropagation();
    const newValue = event.detail?.selectedValue;
    const customAssignationContainerIsDisabled = newValue === PolicyAssignationType.INHERITED;
    toggleDisabled(this.customAssignationContainer, customAssignationContainerIsDisabled);
    if (!customAssignationContainerIsDisabled) {
      this.policyAssignTypeFormField.validate();
    } else {
      this.enforcedFormField.value = false;
    }
  }

  private handlePolicyFilterBackendLoaded(event: CustomEvent) {
    if (!event.detail || !event.detail.filterOptions) {
      console.error('No available policies.');
    }

    this.policyFilterInitialItems = event.detail.filterOptions;

    if (this.policyFilterInitialItems.length !== 0) {
      const firstAvailablePolicyId = this.policyFilterInitialItems[0].value;
      this.policyFilter.setFormValue(firstAvailablePolicyId);
    } else {
      console.error('Could not set first available policy.');
    }
  }

  private getNumberOfEnforcedTargets(): number {
    if (this.enforcedTargetIds) {
      return this.enforcedTargetIds.length;
    } else {
      this.enforcedTargetIds = [];
    }

    let result = 0;
    for (const target of this.targets) {
      if (target.activePolicy?.inheritedFrom?.id && target.activePolicy?.enforced) {
        result++;
        this.enforcedTargetIds.push(target._id);
      }
    }

    return result;
  }

  private handleExcludeTargets() {
    if (!this.enforcedTargetIds) {
      console.error('Could not exclude targets. No targets that meet criteria found.');
      return;
    }

    // filter targets
    this.targets = this.targets.filter(target => !this.enforcedTargetIds?.includes(target._id));
    this.enforcedTargetIds = undefined;

    // reset and rebuild grids
    this.targetsGrid.dependenciesInjected = false;
    this.targetsGrid.started = false;
    this.childGroupsGrid.dependenciesInjected = false;
    this.childGroupsGrid.started = false;

    //clear the wrapper of the grids
    this.targetsGrid.controller?.view.clearWrapper();
    this.childGroupsGrid.controller?.view.clearWrapper();
    this.buildGrids();

    const numberOfEnforcedTargets = this.getNumberOfEnforcedTargets();
    if (numberOfEnforcedTargets === 0) {
      // this should always be true
      this.dispatchEvent(
        new CustomEvent(NetworkPopUpEvents.SET_CONFIRM_BUTTON, {
          bubbles: true,
          composed: true,
          detail: { value: false },
        })
      );
    }
  }
}
